/**
 * What to call a tool on screen.
 *
 * A port of `bimgo-ai-web`'s `getToolDisplayLabel` (`Message.tsx`), which exists
 * because tool names are written for the model, not for the reader.
 * `send_code_to_revit` is precise and tells a user nothing they wanted to know;
 * "Generate Code" tells them what is about to happen to their model. Several
 * tools also collapse onto one label on purpose — four different document
 * lookups are one activity from the outside, and naming each separately turns a
 * single step into four rows that look like four different things.
 *
 * **One departure from the reference, and it is deliberate.** Its fallback is
 * the flat word "Tool" for anything unmapped. That fits 2.x, whose tool set is
 * almost exactly the list below. 2.5 registers a good deal more
 * (`color_splash`, `room_collision_analysis`, `create_section_box`,
 * `get_selected_elements`, …), and rendering all of them as "Tool" would undo
 * the point of naming tools at all — the reader could no longer tell reading
 * from writing. So every tool 2.5 registers has a label of its own in both
 * languages (`OWN_LABELS`, owner 2026-09-11: the humanised English name —
 * "Get current view info", "Inspect revit with code" — is what a Chinese
 * reader saw). The humaniser remains only for a tool a *newer* agent sends
 * that this build has never heard of, so it stays legible rather than hidden.
 *
 * **The two skill-running tools are the exception**, mapped back to the
 * reference's generic label on purpose (owner, 2026-09-01, #58 item 5): see
 * `RUNS_A_SKILL`. Humanising them produced "Execute skill from path", which
 * names the mechanism rather than the act, and *which* skill is the approval
 * card's job.
 *
 * Returning a **key** rather than a translated string keeps this pure: the
 * mapping is the part worth testing, and it needs no locale to test.
 */

/** The tools the reference names, by the identity the agent sends. */
export const TOOL_NAMES = {
  SEND_CODE_TO_REVIT: "send_code_to_revit",
  SEND_SKILL_CANDIDATE: "send_skill_candidate_to_revit",
  SEND_CODE_TO_AUTOCAD: "send_code_to_autocad",
  EXECUTE_SKILL_FROM_PATH: "execute_skill_from_path",
  EXECUTE_CLOUD_SKILL: "execute_cloud_skill",
  RETRIEVE_REVIT_DOC: "retrieve_revit_doc_tool",
  SEARCH_REVIT_DOCS: "search_revit_docs_tool",
  SEARCH_REVIT_LIBRARY: "search_revit_library_tool",
  RESOLVE_REVIT_DOCS: "resolve_revit_docs_tool",
  FILTER_ELEMENTS: "filter_elements",
  COMMIT_SKILL_VERSION: "commit_skill_version",
  SAVE_SKILL: "save_skill",
  SUBMIT_PLAN: "submit_plan",
  ASK_USER: "ask_user",
} as const;

/**
 * The tools whose artifact is generated code — the only ones that offer
 * "Show Source Code" and Save Skill (owner, 2026-08-25: "only Generate code");
 * the old web's `toolToDeliverableMap` was keyed on `send_code_to_revit` alone.
 * AutoCAD joined once its skills had a store (owner, 2026-10-24).
 */
export function generatesCode(tool: string): boolean {
  return tool === TOOL_NAMES.SEND_CODE_TO_REVIT || tool === TOOL_NAMES.SEND_CODE_TO_AUTOCAD;
}

/**
 * Writing C# and running it against the open model.
 *
 * Both CAD hosts share the label: from the reader's side the activity is the
 * same, and which host is being driven is already answered by the status bar.
 */
const GENERATES_CODE: readonly string[] = [
  TOOL_NAMES.SEND_CODE_TO_REVIT,
  TOOL_NAMES.SEND_CODE_TO_AUTOCAD,
  TOOL_NAMES.SEND_SKILL_CANDIDATE,
];

/**
 * Running a saved or a cloud skill.
 *
 * The reference maps neither, so both land on its generic label — and that is
 * deliberate here rather than an oversight of the humanising rule above (owner,
 * 2026-09-01, #58 item 5). `execute_skill_from_path` humanised to "Execute skill
 * from path", which is the API's vocabulary rather than the user's; the label
 * that answers *which* skill belongs to the approval card, which already derives
 * it from the arguments. The row says only that a skill ran.
 */
const RUNS_A_SKILL: readonly string[] = [
  TOOL_NAMES.EXECUTE_SKILL_FROM_PATH,
  TOOL_NAMES.EXECUTE_CLOUD_SKILL,
];

/** Four ways of reading documentation, which is one activity to a reader. */
const LOOKS_UP_DOCS: readonly string[] = [
  TOOL_NAMES.RETRIEVE_REVIT_DOC,
  TOOL_NAMES.SEARCH_REVIT_DOCS,
  TOOL_NAMES.SEARCH_REVIT_LIBRARY,
  TOOL_NAMES.RESOLVE_REVIT_DOCS,
];

/** The four skills-library file tools: browsing one library, from the reader's side. */
const READS_LIBRARY: readonly string[] = ["ls", "read_file", "glob", "grep"];

/**
 * Every other tool 2.5 registers, each with its own label. Keyed by the name the
 * agent sends (`src/agent/src/tools/*.ts` `name:`), valued by the string key,
 * so `toolLabel.test.mjs` can prove each resolves in both languages.
 */
const OWN_LABELS: Readonly<Record<string, string>> = {
  stage_skill_candidate: "skillEdit.titlePrepareChanges",
  inspect_revit_with_code: "message.toolInspectModel",
  get_current_view_info: "message.toolGetCurrentView",
  get_selected_elements: "message.toolGetSelectedElements",
  get_available_family_types: "message.toolGetFamilyTypes",
  highlight_elements: "message.toolHighlightElements",
  color_splash: "message.toolColorSplash",
  create_section_box: "message.toolCreateSectionBox",
  room_collision_analysis: "message.toolRoomCollision",
  check_roadway_clearance: "message.toolRoadwayClearance",
  check_wall_top_capping: "message.toolWallTopCapping",
  find_roadway_floor: "message.toolFindRoadwayFloor",
  generate_revit_code: "message.toolDraftCode",
  load_cloud_skill: "message.toolLoadSkill",
  save_skill: "message.toolSaveSkill",
  [TOOL_NAMES.SUBMIT_PLAN]: "message.toolSubmitPlan",
  [TOOL_NAMES.ASK_USER]: "message.toolAskUser",
};

/**
 * The i18n key for this tool's label, or null when the reference maps none.
 *
 * Null rather than a default key, so the caller decides what an unmapped tool
 * looks like — see the header note on why that is not the reference's "Tool".
 */
export function toolLabelKey(tool: string): string | null {
  if (GENERATES_CODE.includes(tool)) return "message.toolGenerateCode";
  if (LOOKS_UP_DOCS.includes(tool)) return "message.toolLookUpDocs";
  if (tool === TOOL_NAMES.FILTER_ELEMENTS) return "message.toolFilterElements";
  if (tool === TOOL_NAMES.COMMIT_SKILL_VERSION) return "skillEdit.titleCommit";
  if (RUNS_A_SKILL.includes(tool)) return "message.toolUseTool";
  if (READS_LIBRARY.includes(tool)) return "message.toolReadLibrary";
  return OWN_LABELS[tool] ?? null;
}

/**
 * `create_section_box` → "Create section box".
 *
 * Sentence case, not Title Case: a tool name is a description of an action, and
 * Title Case makes every row read like a proper noun. Underscores collapse to
 * spaces because that is all they ever stood for.
 */
export function humanizeToolName(tool: string): string {
  const words = tool.replace(/_/g, " ").trim().replace(/\s+/g, " ");
  // `charAt`, not `[0]`: a name that is nothing but underscores collapses to an
  // empty string here, and indexing that would throw inside a transcript row.
  // An explicit `if (!words) return ""` stood here and has been removed — a
  // mutation survived its deletion, which is the proof it was dead code.
  return words.charAt(0).toUpperCase() + words.slice(1);
}
